import { PageController } from '@client/components/_core/layout/PageController';
import { BasicTabs } from '@client/components/_core/navigation/Tabs';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { formatExtractionMethod } from '@client/components/_shared/documents/extraction-method';
import { ProcessBatchButton, ProcessFileButton } from '@client/components/_shared/documents/ProcessFileButton';
import { ProcessingStatusBadge } from '@client/components/_shared/documents/ProcessingStatusBadge';
import { SearchResultsList } from '@client/components/_shared/documents/SearchResultCard';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { SegmentedControl } from '@client/components/_ui/data-entry/SegmentedControl';
import { FileLink } from '@client/components/_ui/display/FileLink';
import { Tooltip } from '@client/components/_ui/display/Tooltip';
import { User } from '@client/components/_ui/display/User';
import { EmptyState } from '@client/components/_ui/feedback/EmptyState';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { message } from '@client/components/_ui/feedback/message';
import { Space } from '@client/components/_ui/layout/Space';
import { FilterBar } from '@client/components/filters';
import { isWhereClauseEmpty } from '@client/components/filters/core/where-builder';
import { TableViewsBar, useTableViews } from '@client/components/table-views';
import { ICONS } from '@client/constants/icons';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { usePermissions } from '@client/hooks/auth/usePermissions';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { createColumnHelper, PrismaTable, TableEmptyState, usePrismaTable } from '@client/hooks/table-v2';
import { useDebouncedValue } from '@client/hooks/utils/useDebouncedValue';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { type RouterInputs, type RouterOutputs, trpc } from '@client/trpc/client';
import { downloadBlob } from '@client/utils/common/blob';
import {
    CLAIMS_DOCUMENT_CATEGORIES,
    DEFAULT_PAGE_SIZE,
    DOCUMENT_CATEGORY_DISPLAY_NAMES,
    DOCUMENT_PROCESS_BATCH_MAX_FILES,
    FINANCIAL_DOCUMENT_CATEGORIES,
    IDENTITY_DOCUMENT_CATEGORIES,
    INSURANCE_POLICY_CATEGORIES,
    LEGAL_DOCUMENT_CATEGORIES,
} from '@shared/constants';
import type { DocumentCategory, ExtractionStatus } from '@shared/types';
import { fileExtension, formatFileSize } from '@shared/utils/core/file';
import { createFileRoute, Outlet } from '@tanstack/react-router';
// curium:allow-spin — inline leaf-node debounce indicator in the search input suffix.
import { Alert, Card, Input, Select, Slider, Spin, Tag, Typography } from 'antd';
import type { Key } from 'antd/es/table/interface';
import JSZip from 'jszip';
import { Brain, CheckCircle, CircleHelp, Combine, Download, FileSearch, Search, Type, X, XCircle } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    BULK_DOWNLOAD_MAX_FILES,
    BulkDocumentDownloadError,
    downloadSelectedDocuments,
} from './bulk-document-download';
import { useDocumentFilterConfig } from './DocumentPageFilters';
import { DOCUMENT_BUILT_IN_VIEWS } from './document-built-in-views';

export const Route = createFileRoute('/console/$orgId/documents')({
    staticData: {
        breadcrumb: { title: 'Documents' },
        ui: {
            icon: ICONS.documents,
            title: 'Documents',
            subtitle: 'Search and analyse documents across your organisation',
            widthVariant: 'fullWidth',
        },
        roles: ['user', 'shared_edit_user', 'org_admin'],
        featureFlag: 'enable-document-service',
    },
    component: DocumentsLayout,
});

type File = RouterOutputs['file']['listFiles']['rows'][number];
type FileWhere = NonNullable<RouterInputs['file']['listFiles']['where']>;
type OrderBy = NonNullable<RouterInputs['file']['listFiles']['orderBy']>;

// Module scope: a helper created per render would sit in the `useMemo` deps and defeat it.
const columnHelper = createColumnHelper<File, OrderBy>();

// ─── Inline Category Select ─────────────────────────────────

function InlineCategorySelect({
    fileId,
    value,
    onSuccess,
}: {
    fileId: string;
    value: DocumentCategory | null;
    onSuccess: () => void;
}) {
    const mutation = trpc.file.updateDocumentCategory.useMutation({
        onSuccess,
        onError: () => message.error('Failed to update category'),
    });

    return (
        <Select
            variant="borderless"
            size="small"
            value={(value as DocumentCategory | undefined) ?? undefined}
            placeholder="—"
            options={GROUPED_CATEGORY_OPTIONS}
            popupMatchSelectWidth={false}
            showSearch
            optionFilterProp="label"
            className="w-full text-xs"
            loading={mutation.isPending}
            onClick={(e) => e.stopPropagation()}
            onChange={(val) => {
                if (val) {
                    mutation.mutate({ fileId, category: val as DocumentCategory });
                }
            }}
        />
    );
}

// ─── All Documents Tab ───────────────────────────────────────

function AllDocumentsTab({ onSearchSimilar }: { onSearchSimilar?: (query: string) => void }) {
    const { formatDateTime } = useFormatter();
    const { trpcUtils, trpcClient } = useTrpc();
    const { isAdmin } = usePermissions();
    const navigate = useNavigate();
    const orgId = useOrgId();
    const {
        config: filterConfig,
        ready: filterConfigReady,
        isPending: filterConfigPending,
        error: filterConfigError,
        refetch: refetchFilterConfig,
    } = useDocumentFilterConfig();

    // Row selection state
    const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);

    const baseWhere: FileWhere = useMemo(
        () => ({
            OR: [{ extractionStatus: { not: null } }, { documentCategory: { not: null } }],
        }),
        [],
    );

    const [input, setInput] = usePathState<RouterInputs['file']['listFiles']>('query-params', {
        orderBy: [{ createdAt: 'desc' }],
        where: {},
    });

    const [viewsReady, setViewsReady] = useState(false);

    // Merge filter where with base where
    const mergedInput = useMemo(
        (): RouterInputs['file']['listFiles'] => ({
            ...input,
            where: {
                AND: [baseWhere, input.where ?? {}],
            },
        }),
        [input, baseWhere],
    );

    const filterConfigSettledWithError = !filterConfigPending && filterConfigError != null;
    const listReady = viewsReady || filterConfigSettledWithError;

    const { data, isPending, refetch } = trpc.file.listFiles.useQuery(mergedInput, {
        enabled: listReady,
        refetchInterval: (query) => {
            const hasProcessingFiles = query.state.data?.rows.some(
                (file) => file.extractionStatus === 'PROCESSING' || file.extractionStatus === 'PENDING',
            );
            return hasProcessingFiles ? 5000 : false;
        },
    });

    const handleDownloadFile = useCallback(
        async (key: string) => {
            const url = await trpcUtils.file.getDownloadUrl.fetch({ key });
            window.open(url, '_blank');
        },
        [trpcUtils],
    );

    const columns = useMemo(
        () => [
            columnHelper.accessor('key', {
                id: 'name',
                header: 'Name',
                sortable: 'key',
                cell: ({ row }) => <FileLink fileKey={row.key} fileName={row.name} />,
            }),
            columnHelper.accessor('extractionStatus', {
                id: 'extractionStatus',
                header: 'Processing',
                sortable: 'extractionStatus',
                cell: ({ row }) => (
                    <ProcessingStatusBadge
                        status={row.extractionStatus as ExtractionStatus}
                        error={row.extractionError}
                        mimeType={row.mimeType}
                        invalidContentReason={row.invalidContentReason}
                    />
                ),
                width: 120,
            }),
            columnHelper.accessor('documentCategory', {
                id: 'documentCategory',
                header: 'Category',
                sortable: 'documentCategory',
                cell: ({ row }) => (
                    <InlineCategorySelect
                        fileId={row.id}
                        value={row.documentCategory as DocumentCategory | null}
                        onSuccess={() => void refetch()}
                    />
                ),
                width: 170,
            }),
            columnHelper.accessor('documentSummary', {
                id: 'summary',
                header: 'Summary',
                cell: ({ row }) =>
                    row.documentSummary ? (
                        <Tooltip title={row.documentSummary}>
                            <Typography.Paragraph ellipsis={{ rows: 2 }} className="!mb-0 text-xs">
                                {row.documentSummary}
                            </Typography.Paragraph>
                        </Tooltip>
                    ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                    ),
                width: 200,
            }),
            columnHelper.accessor('keywords', {
                id: 'keywords',
                header: 'Keywords',
                cell: ({ row }) => {
                    const keywords = row.keywords;
                    if (!keywords?.length) return <span className="text-muted-foreground text-xs">—</span>;
                    const visible = keywords.slice(0, 3);
                    const overflow = keywords.length - 3;
                    return (
                        <div className="flex flex-wrap gap-1">
                            {visible.map((kw) => (
                                <Tag key={kw} variant="outlined" className="!m-0 text-xs">
                                    {kw}
                                </Tag>
                            ))}
                            {overflow > 0 && (
                                <Tooltip title={keywords.slice(3).join(', ')}>
                                    <Tag variant="outlined" className="!m-0 text-xs">
                                        +{overflow}
                                    </Tag>
                                </Tooltip>
                            )}
                        </div>
                    );
                },
                width: 180,
            }),
            columnHelper.accessor('categoryConfidence', {
                id: 'confidence',
                header: 'Confidence',
                sortable: 'categoryConfidence',
                cell: ({ row }) => {
                    if (row.categoryConfidence == null) {
                        return <span className="text-muted-foreground text-xs">—</span>;
                    }
                    const percent = Math.round(row.categoryConfidence * 100);
                    const colorClass =
                        percent >= 80
                            ? 'text-green-600 dark:text-green-400'
                            : percent >= 60
                              ? 'text-yellow-600 dark:text-yellow-400'
                              : 'text-red-600 dark:text-red-400';
                    return <span className={`font-medium text-xs ${colorClass}`}>{percent}%</span>;
                },
                align: 'center',
                width: 90,
            }),
            columnHelper.accessor('extractionMethod', {
                id: 'extractionMethod',
                header: 'Extraction method',
                cell: ({ row }) =>
                    row.extractionMethod ? (
                        <span className="text-xs">{formatExtractionMethod(row.extractionMethod)}</span>
                    ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                    ),
                width: 130,
            }),
            columnHelper.accessor('embeddingId', {
                id: 'indexed',
                header: 'Indexed',
                cell: ({ row }) =>
                    row.embeddingId ? (
                        <Tooltip title="Indexed for semantic search">
                            <CheckCircle size={14} className="text-green-600 dark:text-green-400" />
                        </Tooltip>
                    ) : (
                        <Tooltip title="Not indexed">
                            <XCircle size={14} className="text-muted-foreground" />
                        </Tooltip>
                    ),
                align: 'center',
                width: 70,
            }),
            columnHelper.accessor('name', {
                id: 'fileType',
                header: 'Type',
                cell: ({ row }) => fileExtension(row.name),
                align: 'center',
                width: 80,
            }),
            columnHelper.accessor('size', {
                id: 'size',
                header: 'Size',
                sortable: 'size',
                cell: ({ row }) => (row.size ? formatFileSize(row.size) : ''),
                width: 100,
            }),
            columnHelper.accessor('createdAt', {
                id: 'uploaded',
                header: 'Uploaded',
                sortable: 'createdAt',
                cell: ({ row }) => formatDateTime(row.createdAt),
                width: 150,
            }),
            columnHelper.display({
                id: 'uploadedBy',
                header: 'Uploaded by',
                cell: ({ row }) => (
                    <User
                        userId={row.CreatedBy?.userId}
                        name={row.CreatedBy?.fullName}
                        photoUrl={row.CreatedBy?.photoUrl}
                        size="medium"
                        showPopover={false}
                    />
                ),
                width: 150,
            }),
            columnHelper.actions({
                cell: ({ row }) => {
                    const canReprocess =
                        isAdmin &&
                        (row.extractionStatus === 'COMPLETED' ||
                            row.extractionStatus === 'FAILED' ||
                            row.extractionStatus === 'PROCESSING' ||
                            row.extractionStatus === 'PENDING');
                    const canViewExtraction = row.extractionStatus === 'COMPLETED';

                    return (
                        <Space size="small">
                            {canViewExtraction && (
                                <Button.Icon
                                    icon={<FileSearch />}
                                    color="purple"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        navigate({
                                            to: '/console/$orgId/documents/details/$fileId',
                                            params: { orgId, fileId: row.id },
                                        });
                                    }}
                                    tooltip="View extraction"
                                />
                            )}
                            <Button.Download
                                onClick={(e) => {
                                    e.stopPropagation();
                                    void handleDownloadFile(row.key);
                                }}
                            />
                            {row.keywords?.length > 0 && onSearchSimilar && (
                                <Button.Icon
                                    icon={<Search />}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onSearchSimilar(row.keywords.slice(0, 5).join(' '));
                                    }}
                                    tooltip="Search similar"
                                />
                            )}
                            {canReprocess && (
                                <ProcessFileButton
                                    fileId={row.id}
                                    extractionStatus={row.extractionStatus}
                                    variant="icon"
                                    force
                                    onSuccess={() => void refetch()}
                                />
                            )}
                        </Space>
                    );
                },
                width: 160,
            }),
        ],
        [formatDateTime, isAdmin, navigate, orgId, refetch, handleDownloadFile, onSearchSimilar],
    );

    // `input.where` holds only the user's filters and the active view's scope, never `baseWhere`, so a
    // built-in view such as Needs attention counts as filtered and says nothing matches rather than
    // that no documents exist.
    const hasActiveFilters = !isWhereClauseEmpty(input.where);

    const tableProps = usePrismaTable({
        data: data?.rows || [],
        total: data?.total || 0,
        loading: !listReady || isPending,
        columns,
        rowKey: 'id',
        queryParams: input,
        onParamsChange: setInput,
        pagination: {
            showSizeChanger: true,
        },
        columnVisibility: {
            enabled: true,
            persistKey: 'documents-table-columns',
            defaultHidden: ['keywords', 'confidence', 'extractionMethod', 'indexed', 'fileType', 'size', 'uploadedBy'],
        },
        rowSelection: {
            enabled: true,
            type: 'checkbox',
            preserveSelectedRowKeys: true,
            selectedRowKeys,
            onChange(keys) {
                setSelectedRowKeys(keys);
            },
        },
        size: 'small',
        scroll: { x: 'max-content' },
        emptyContent: (
            <TableEmptyState
                hasActiveFilters={hasActiveFilters}
                noRecordsTitle="No documents to show yet"
                noRecordsDescription="Processed or categorised documents on the claims, complaints and incidents you can open appear here."
                noMatchesTitle="No documents match your filters"
            />
        ),
    });

    const views = useTableViews({
        entityType: 'DOCUMENT',
        config: filterConfig,
        configReady: filterConfigReady,
        urlFilterKey: 'doc-filters',
        builtInViews: DOCUMENT_BUILT_IN_VIEWS,
        queryParams: input,
        setQueryParams: setInput,
        table: tableProps,
        columnPersistKey: 'documents-table-columns',
        defaultOrderBy: [{ createdAt: 'desc' }] as const,
        defaultPageSize: DEFAULT_PAGE_SIZE,
        defaultHidden: ['keywords', 'confidence', 'extractionMethod', 'indexed', 'fileType', 'size', 'uploadedBy'],
        onResolved: () => setViewsReady(true),
    });

    const processingCount = data?.rows.filter(
        (f) => f.extractionStatus === 'PROCESSING' || f.extractionStatus === 'PENDING',
    ).length;

    // One deduplicated ID list drives the count, Download and Process, so a selection kept across
    // pages or views is acted on in full rather than intersected with the rows currently shown.
    const selectedIds = useMemo(() => [...new Set(selectedRowKeys.map(String))], [selectedRowKeys]);
    const hiddenSelectedCount = useMemo(() => {
        if (!data || isPending) return 0;
        const visibleIds = new Set(data.rows.map((row) => row.id));
        return selectedIds.filter((id) => !visibleIds.has(id)).length;
    }, [data, isPending, selectedIds]);
    const exceedsProcessLimit = selectedIds.length > DOCUMENT_PROCESS_BATCH_MAX_FILES;
    const exceedsDownloadLimit = selectedIds.length > BULK_DOWNLOAD_MAX_FILES;

    const [bulkDownloading, setBulkDownloading] = useState(false);
    const bulkDownloadInFlight = useRef(false);
    const handleBulkDownload = useCallback(async () => {
        if (bulkDownloadInFlight.current) return;
        const fileIds = selectedIds;
        if (fileIds.length === 0 || fileIds.length > BULK_DOWNLOAD_MAX_FILES) return;

        bulkDownloadInFlight.current = true;
        setBulkDownloading(true);
        try {
            await downloadSelectedDocuments(fileIds, {
                mintUrl: (fileId) => trpcClient.file.getFileDownloadUrl.query({ fileId }),
                async fetchBinary(url) {
                    const response = await fetch(url);
                    if (!response.ok) throw new Error(`Download failed: HTTP ${response.status}`);
                    return response.blob();
                },
                saveBlob: downloadBlob,
                async createArchive(entries) {
                    const zip = new JSZip();
                    for (const { name, blob } of entries) zip.file(name, blob);
                    return zip.generateAsync({ type: 'blob' });
                },
            });
        } catch (err) {
            console.error('Bulk download failed:', err);
            message.error(
                err instanceof BulkDocumentDownloadError
                    ? `${err.failedCount} of ${err.totalCount} selected documents could not be downloaded, so no archive was saved. They may have been deleted or you may no longer have access. Try again, or clear the selection.`
                    : fileIds.length === 1
                      ? 'The selected document could not be downloaded. It may have been deleted or you may no longer have access. Try again, or clear the selection.'
                      : 'The selected documents could not be downloaded. Try again, or clear the selection.',
                8,
            );
        } finally {
            bulkDownloadInFlight.current = false;
            setBulkDownloading(false);
        }
    }, [selectedIds, trpcClient]);

    return (
        <>
            <TableViewsBar controller={views.controller} groupLabel="Document views" />

            {filterConfigError && (
                <InlineQueryError
                    error={filterConfigError}
                    onRetry={() => void refetchFilterConfig()}
                    title="Could not load document filters"
                    compact
                    className="mb-3"
                />
            )}

            <TableToolbar
                filters={
                    <FilterBar
                        config={filterConfig}
                        filters={views.filterState.filters}
                        whereClause={views.filterState.whereClause}
                        onAddFilter={views.filterState.addFilter}
                        onUpdateFilter={views.filterState.updateFilter}
                        onRemoveFilter={views.filterState.removeFilter}
                        onClearFilters={views.filterState.clearFilters}
                    />
                }
                variant="enhanced"
                actions={
                    <Space size="small">
                        {processingCount != null && processingCount > 0 && (
                            <span className="whitespace-nowrap text-muted-foreground text-sm">
                                {processingCount} of {data?.rows.length} processing...
                            </span>
                        )}
                        {selectedIds.length > 0 && (
                            <>
                                <span
                                    className="whitespace-nowrap text-muted-foreground text-sm"
                                    data-testid="documents-selection-count"
                                >
                                    {selectedIds.length} selected
                                    {hiddenSelectedCount > 0 && ` (${hiddenSelectedCount} not shown on this page)`}
                                </span>
                                {exceedsProcessLimit && (
                                    <span className="whitespace-nowrap text-muted-foreground text-sm">
                                        Process up to {DOCUMENT_PROCESS_BATCH_MAX_FILES} documents at a time
                                    </span>
                                )}
                                <Tooltip
                                    title={
                                        exceedsProcessLimit
                                            ? `Select ${DOCUMENT_PROCESS_BATCH_MAX_FILES} or fewer documents to process`
                                            : undefined
                                    }
                                >
                                    <span className="inline-flex">
                                        <ProcessBatchButton
                                            fileIds={selectedIds}
                                            force
                                            disabled={exceedsProcessLimit}
                                            onSuccess={() => {
                                                void refetch();
                                            }}
                                        />
                                    </span>
                                </Tooltip>
                                {exceedsDownloadLimit && (
                                    <span className="whitespace-nowrap text-muted-foreground text-sm">
                                        Download up to {BULK_DOWNLOAD_MAX_FILES} documents at a time
                                    </span>
                                )}
                                <Tooltip
                                    title={
                                        exceedsDownloadLimit
                                            ? `Select ${BULK_DOWNLOAD_MAX_FILES} or fewer documents to download`
                                            : undefined
                                    }
                                >
                                    <span className="inline-flex">
                                        <Button
                                            type="tertiary"
                                            size="small"
                                            icon={<Download />}
                                            loading={bulkDownloading}
                                            disabled={bulkDownloading || exceedsDownloadLimit}
                                            onClick={() => void handleBulkDownload()}
                                        >
                                            Download
                                        </Button>
                                    </span>
                                </Tooltip>
                                <Button
                                    type="tertiary"
                                    size="small"
                                    icon={<X />}
                                    onClick={() => setSelectedRowKeys([])}
                                >
                                    Clear
                                </Button>
                            </>
                        )}
                    </Space>
                }
            />
            <PrismaTable
                {...tableProps}
                onRow={({ id, extractionStatus, key }) => ({
                    className: 'cursor-pointer',
                    onClick() {
                        if (extractionStatus === 'COMPLETED') {
                            navigate({
                                to: '/console/$orgId/documents/details/$fileId',
                                params: { orgId, fileId: id },
                            });
                        } else {
                            void handleDownloadFile(key);
                        }
                    },
                })}
            />
        </>
    );
}

// ─── Search Documents Tab ────────────────────────────────────

type SearchMode = 'semantic' | 'hybrid' | 'keyword';

const SEARCH_MODE_PLACEHOLDERS: Record<SearchMode, string> = {
    semantic: "Search documents by meaning, for example 'insurance policy renewal'",
    hybrid: "Search by content or meaning, for example 'policy renewal clause'",
    keyword: "Search by keywords, for example 'policy premium schedule'",
};

const SEARCH_MODE_OPTIONS = [
    {
        label: 'Semantic',
        value: 'semantic' as const,
        icon: <Brain size={14} />,
        tooltip: 'Search by meaning using AI embeddings',
    },
    {
        label: 'Hybrid',
        value: 'hybrid' as const,
        icon: <Combine size={14} />,
        tooltip: 'Combine keyword matching with semantic search',
    },
    {
        label: 'Keyword',
        value: 'keyword' as const,
        icon: <Type size={14} />,
        tooltip: 'Search by exact keyword matching (BM25)',
    },
];

const NAMED_GROUP_CATEGORIES = new Set<string>([
    ...FINANCIAL_DOCUMENT_CATEGORIES,
    ...INSURANCE_POLICY_CATEGORIES,
    ...CLAIMS_DOCUMENT_CATEGORIES,
    ...IDENTITY_DOCUMENT_CATEGORIES,
    ...LEGAL_DOCUMENT_CATEGORIES,
]);

const mapCategoryOptions = (categories: readonly DocumentCategory[]) =>
    categories.map((c) => ({ label: DOCUMENT_CATEGORY_DISPLAY_NAMES[c], value: c }));

const GROUPED_CATEGORY_OPTIONS = [
    { label: 'Financial', options: mapCategoryOptions(FINANCIAL_DOCUMENT_CATEGORIES) },
    { label: 'Insurance policy', options: mapCategoryOptions(INSURANCE_POLICY_CATEGORIES) },
    { label: 'Claims', options: mapCategoryOptions(CLAIMS_DOCUMENT_CATEGORIES) },
    { label: 'Identity', options: mapCategoryOptions(IDENTITY_DOCUMENT_CATEGORIES) },
    { label: 'Legal', options: mapCategoryOptions(LEGAL_DOCUMENT_CATEGORIES) },
    {
        label: 'Other',
        options: (Object.keys(DOCUMENT_CATEGORY_DISPLAY_NAMES) as DocumentCategory[])
            .filter((c) => !NAMED_GROUP_CATEGORIES.has(c))
            .map((c) => ({ label: DOCUMENT_CATEGORY_DISPLAY_NAMES[c], value: c })),
    },
];

function SearchDocumentsTab() {
    const { trpcUtils } = useTrpc();
    const navigate = useNavigate();
    const orgId = useOrgId();
    const [persistedQuery, setPersistedQuery] = usePathState('doc-search-q', '');
    const [searchQuery, setSearchQuery] = useState(persistedQuery);
    const [searchMode, setSearchMode] = usePathState<SearchMode>('doc-search-mode', 'hybrid');
    const [similarityThresholdOverride, setSimilarityThresholdOverride] = useState<number>();
    const [categoryFilter, setCategoryFilter] = usePathState<string | undefined>('doc-search-cat', undefined);
    const [sortBy, setSortBy] = useState<'relevance' | 'date' | 'name'>('relevance');
    const [resultLimit, setResultLimit] = useState(20);
    const debouncedQuery = useDebouncedValue(searchQuery, 300);
    const isDebouncing = searchQuery !== debouncedQuery;
    const { data: searchConfiguration } = trpc.documentProcessing.getSearchConfiguration.useQuery();
    const similarityThreshold = similarityThresholdOverride ?? searchConfiguration?.defaultMinSimilarity;

    // Sync debounced query to URL (not per-keystroke)
    useEffect(() => {
        setPersistedQuery(debouncedQuery);
    }, [debouncedQuery, setPersistedQuery]);

    // Sync URL → local when navigated to with a query (e.g., "Search similar").
    // Deliberately keyed on the URL value only: a local edit must not re-seed it.
    useEffect(() => {
        if (persistedQuery && !searchQuery) {
            setSearchQuery(persistedQuery);
        }
    }, [persistedQuery]);

    const {
        data: searchResults,
        isFetching,
        error,
    } = trpc.documentProcessing.searchDocuments.useQuery(
        {
            query: debouncedQuery,
            searchMode,
            minSimilarity: searchMode === 'semantic' ? similarityThreshold : undefined,
            limit: resultLimit,
            documentCategory: categoryFilter,
            sortBy,
        },
        {
            enabled: debouncedQuery.length >= 3 && (searchMode !== 'semantic' || similarityThreshold !== undefined),
            placeholderData: (prev) => prev,
        },
    );

    const handleView = useCallback(
        (fileId: string) => {
            navigate({
                to: '/console/$orgId/documents/details/$fileId',
                params: { orgId, fileId },
            });
        },
        [navigate, orgId],
    );

    const handleDownload = useCallback(
        async (fileId: string) => {
            const file = await trpcUtils.file.getFile.fetch({ id: fileId });
            if (file) {
                const url = await trpcUtils.file.getDownloadUrl.fetch({ key: file.key });
                window.open(url, '_blank');
            }
        },
        [trpcUtils],
    );

    const sortOptions = [
        { label: 'Relevance', value: 'relevance' as const },
        { label: 'Date', value: 'date' as const },
        { label: 'Name', value: 'name' as const },
    ];

    const hasQuery = debouncedQuery.length >= 3;
    const showResults = searchResults && hasQuery;

    return (
        <div className="space-y-6">
            {/* Search Input */}
            <Card>
                <div className="space-y-4">
                    <div className="flex items-center gap-3">
                        <span className="text-muted-foreground text-sm">Search mode:</span>
                        <SegmentedControl
                            value={searchMode}
                            onChange={setSearchMode}
                            options={SEARCH_MODE_OPTIONS}
                            aria-label="Search mode"
                            size="small"
                        />
                        <Button.Icon
                            icon={<CircleHelp />}
                            color="gray"
                            size="small"
                            aria-label="Explain search modes"
                            tooltip={
                                <div className="space-y-2 text-xs">
                                    <p className="m-0">
                                        <strong>Semantic:</strong> Finds documents with similar meaning, even without
                                        exact words. Best for natural language queries.
                                    </p>
                                    <p className="m-0">
                                        <strong>Hybrid:</strong> Combines meaning and keyword matching. Best for
                                        general-purpose searching.
                                    </p>
                                    <p className="m-0">
                                        <strong>Keyword:</strong> Finds exact word matches. Best for specific terms like
                                        policy numbers or ABNs.
                                    </p>
                                </div>
                            }
                        />
                    </div>

                    <Input
                        aria-label="Search documents"
                        placeholder={
                            searchQuery.length > 0 && searchQuery.length < 3
                                ? 'Enter at least 3 characters to search...'
                                : SEARCH_MODE_PLACEHOLDERS[searchMode]
                        }
                        size="middle"
                        prefix={<Search className="text-subtle-foreground" size={18} />}
                        suffix={
                            isDebouncing && searchQuery.length >= 3 ? (
                                <Spin size="small" /> // curium:allow-spin
                            ) : undefined
                        }
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        allowClear
                    />

                    {/* Filters row */}
                    <div className="flex items-center gap-4">
                        <Select
                            aria-label="Category"
                            placeholder="Category"
                            allowClear
                            value={categoryFilter}
                            onChange={(val) => setCategoryFilter(val || undefined)}
                            options={GROUPED_CATEGORY_OPTIONS}
                            className="w-56"
                            size="middle"
                            showSearch
                            optionFilterProp="label"
                        />
                        <Select
                            aria-label="Sort by"
                            value={sortBy}
                            onChange={setSortBy}
                            options={sortOptions}
                            className="w-32"
                            size="middle"
                        />
                    </div>

                    {/* Similarity Threshold Slider — only for semantic mode */}
                    {searchMode === 'semantic' && (
                        <div className="flex items-center gap-4">
                            <Tooltip title="Filter out results below this relevance threshold">
                                <span className="whitespace-nowrap text-muted-foreground text-sm">
                                    Min. similarity:
                                </span>
                            </Tooltip>
                            <Slider
                                min={0.3}
                                max={0.95}
                                step={0.05}
                                value={similarityThreshold}
                                onChange={setSimilarityThresholdOverride}
                                disabled={similarityThreshold === undefined}
                                className="max-w-xs flex-1"
                                tooltip={{
                                    formatter: (value) =>
                                        value === undefined ? undefined : `${Math.round(value * 100)}%`,
                                }}
                            />
                            <span className="w-12 font-medium text-sm">
                                {similarityThreshold === undefined ? '—' : `${Math.round(similarityThreshold * 100)}%`}
                            </span>
                        </div>
                    )}
                </div>
            </Card>

            {/* Error State */}
            {error && <Alert type="error" title="Search failed" description={error.message} showIcon />}

            {/* Results — keep previous visible during refetch with opacity overlay */}
            {showResults && (
                <div className={isFetching ? 'pointer-events-none opacity-50' : undefined}>
                    <SearchResultsList
                        results={searchResults.files}
                        query={searchQuery}
                        searchMode={searchMode}
                        hasCategoryFilter={categoryFilter != null}
                        metadata={{
                            searchTimeMs: searchResults.searchTimeMs,
                            filesSearched: searchResults.filesSearched,
                            totalMatches: searchResults.totalMatches,
                        }}
                        onView={handleView}
                        onDownload={handleDownload}
                    />
                    {searchResults.files.length >= resultLimit && resultLimit < 100 && (
                        <div className="mt-4 text-center">
                            <Button type="tertiary" onClick={() => setResultLimit((prev) => Math.min(prev + 20, 100))}>
                                Load more results
                            </Button>
                            <p className="m-0 mt-1 text-muted-foreground text-xs">
                                Showing {searchResults.files.length} of {searchResults.totalMatches} results
                            </p>
                        </div>
                    )}
                </div>
            )}

            {/* Skeleton loading on first search (no previous data) */}
            {isFetching && !searchResults && hasQuery && (
                <SearchResultsList results={[]} loading query={searchQuery} searchMode={searchMode} />
            )}

            {/* Empty State (no search) */}
            {!isFetching && !searchResults && debouncedQuery.length === 0 && (
                <EmptyState
                    icon={<Search />}
                    title="Search across all your documents"
                    description='Use natural language, keywords or both. Try topics like "insurance policy" and "claim settlement" or specific terms like "ABN 123456".'
                />
            )}
        </div>
    );
}

// ─── Layout ──────────────────────────────────────────────────

function DocumentsLayout() {
    const [activeTab, setActiveTab] = usePathState<string>('documents-tab', 'all');
    const [, setSearchQuery] = usePathState('doc-search-q', '');

    const handleSearchSimilar = useCallback(
        (query: string) => {
            setSearchQuery(query);
            setActiveTab('search');
        },
        [setSearchQuery, setActiveTab],
    );

    const tabItems = [
        {
            key: 'all',
            label: 'All documents',
            children: <AllDocumentsTab onSearchSimilar={handleSearchSimilar} />,
        },
        {
            key: 'search',
            label: (
                <span className="flex items-center gap-2">
                    <Search size={14} />
                    Search
                </span>
            ),
            children: <SearchDocumentsTab />,
        },
    ];

    return (
        <PageController route={Route}>
            <BasicTabs items={tabItems} activeKey={activeTab} onChange={setActiveTab} size="large" />
            <Outlet />
        </PageController>
    );
}
